<script>
    import * as THREE from 'three';
    import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
    import { onMount } from 'svelte';
    import Delaunator from 'delaunator';
    import { createTexture } from './colorbars';
    import * as topojson from "topojson-client";
//import { getEnvironmentData } from 'worker_threads';


    const vsSource = `
    //Vertex shader
    in vec4 a_position;
    //uniform float t;
    out float val;
    //uniform mat4 u_matrix;

    uniform mat4 modelViewMatrix;
    uniform mat4 projectionMatrix;

    void main() {

    //Mercator
    //vec4 merc = vec4(vec2(0.3*radians(a_position.x),0.3*log(tan(((3.141592654/2.0) + radians(a_position.y))/2.0))),-1.0,1.0);

    //Orthographic
    //vec4 orth = u_matrix*vec4(vec2(cos(radians(a_position.y))*sin(radians(a_position.x)),cos(radians(a_position.y))*cos(radians(a_position.x)))*vec2(1.,1.),a_position.w,1.);
    
    //gl_Position=vec4(merc.x + t * (orth.x - merc.x), merc.y + t * (orth.y - merc.y),orth.z,1.0);
    gl_Position = projectionMatrix * modelViewMatrix*vec4(a_position.xyz, 1.0);

    val=a_position.w;
    }
    `

    const fsSource = `
    precision mediump float;
    in float val;
    out vec4 color;
    uniform sampler2D u_colorbar;
    //uniform vec2 minmax;


    void main() {
    //float calcolor=(val-minmax.x)/(minmax.y-minmax.x);
    //gl_FragColor = texture2D(u_texture,vec2(min(max(calcolor,0.0),1.0),0.0));
    //color = vec4(val/4.0, 0.0, 0.0, 1.0);
    color = texture(u_colorbar, vec2(val/4.0, 0.0));
    }
    `

    let canvas;
    let scene;
    let camera;
    let renderer;
    let controls;
    let cube;
    let triangulated;
    let mesh;
    let animationId;
    let then;
    let accum = 0;
    let dateText;

    export let state;

    const empty = new Uint8Array([255,255,255,255]);

    const texture2dEmpty = new THREE.DataTexture(empty, 1, 1, THREE.RGBAFormat);
    texture2dEmpty.minFilter = THREE.NearestFilter;
    texture2dEmpty.magFilter = THREE.NearestFilter;
    texture2dEmpty.unpackAlignment = 1;
    texture2dEmpty.needsUpdate = true;

    
    const radius = 25;
    const landRadius = 25.25;

    function vertex([longitude, latitude], radius) {
        const lambda = longitude * Math.PI / 180;
        const phi = latitude * Math.PI / 180;

        //console.log(lambda*180/Math.PI);

        return [
            radius * Math.cos(phi) * Math.cos(lambda),
            radius * Math.sin(phi),
            -radius * Math.cos(phi) * Math.sin(lambda)
        ]    
    }

    function wireframe(multilinestring, radius, material) {
        const geometry = new THREE.BufferGeometry();
       
        //console.log(geometry.attributes.position.array)
        let vertices = [];
        for (const P of multilinestring.coordinates) {
            for (let p0, p1 = vertex(P[0], landRadius), i = 1; i < P.length; ++i) {
                p0 = p1
                vertices.push(p0[0], p0[1], p0[2]);
                p1 = vertex(P[i], landRadius);
                vertices.push(p1[0], p1[1], p1[2]);
            }
        }
        geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
        return new THREE.LineSegments(geometry, material);
    }
       
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ]

    async function getData() {
        try {

            let pwUrl = '';

            await fetch("https://quadweather.com/gfspw")
                .then(d=>d.json())
                .then(d=> {
                    const dateObj = new Date(d[0][0].cycle);
                    const monthNum = dateObj.getUTCMonth();
                    const month = ('0'+(monthNum + 1)).slice(-2); //months from 1-12
                    const day = ('0'+dateObj.getUTCDate()).slice(-2);
                    const year = dateObj.getUTCFullYear();
                    const hour = ('0'+dateObj.getUTCHours()).slice(-2);
                    const ymd = year+month+day;
                 
                    pwUrl = `https://quadweather.com/data/gfs.${ymd}/pwat0_${hour}Z_f0_F.json`;
               
                    dateText = `${hour}Z ${day} ${months[monthNum]} ${year}`
                });

            const [pwat, land] = await Promise.all([
                fetch(pwUrl),
                fetch("land-50m.json")
            ])

            
            pwat.json().then(d => {

                let outarray=[];
                let outarray2=[];

                for (let i=0; i < d.coords.length-2; i+=3) {
                    outarray.push([d.coords[i], d.coords[i+1]])
                    const lat = 360/Math.PI*Math.atan(Math.exp(Math.PI-2*Math.PI*d.coords[i+1])) - 90;
                    const lon = 360*d.coords[i]-180;
                    const sphere = vertex([lon, lat], radius);
                    outarray2.push(sphere[0], sphere[1], sphere[2], d.coords[i+2]);
                }

                triangulated = [
                    Delaunator.from(outarray, function(d) { return d[0];},function(d) {return d[1];}),
                    new Float32Array(outarray2)
                ]

                const materialVolume = new THREE.RawShaderMaterial({
                    glslVersion: THREE.GLSL3,
                    uniforms: {
                        u_colorbar: {value: texture2dEmpty},
                    },
                    vertexShader:vsSource,
                    fragmentShader:fsSource,
                    side: THREE.FrontSide,
                });

                const geometryVolume = new THREE.BufferGeometry();
                geometryVolume.setAttribute('a_position', new THREE.BufferAttribute( triangulated[1], 4 ));
                geometryVolume.setIndex(new THREE.BufferAttribute(triangulated[0].triangles, 1));

                //mesh is combination of geometry and material
                mesh = new THREE.Mesh( geometryVolume, materialVolume );

                const texture = createTexture("pw");
                
                const textureCb = new THREE.Texture(texture.imageData);
                textureCb.needsUpdate = true;

                textureCb.format = THREE.RGBAFormat;
                textureCb.minFilter = THREE.LinearFilter;
                textureCb.magFilter = THREE.LinearFilter;

                mesh.material.uniforms.u_colorbar.value = textureCb;

                mesh.frustumCulled = false;

                scene.add(mesh);

               
                animationId = requestAnimationFrame(animate);


            })

            land.json().then(topology => {
              
                const mesh = topojson.mesh(topology, topology.objects.land);
                const wf = wireframe(mesh, radius, new THREE.LineBasicMaterial({color: 0xffffff,
                    side:THREE.FrontSide
                
                }));
                scene.add(wf);
                //render();
            })

        } catch(err) {
            console.log(err);
        }
    }

    function render() {
        renderer.render(scene, camera);
        
    }

    onMount(() => {
        
        // const observer = new IntersectionObserver(onResize);
        // observer.observe(canvas);
       
        renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            alpha:true
        });
        const element = document.getElementById("can");
    
        const logo = document.getElementById("logo");
        let logoHeight = logo.clientHeight;

        let globeDim = 0.66*window.innerWidth;
        globeDim = Math.min(750, globeDim);

        if (window.innerWidth < 640) {
            globeDim = 0.9*window.innerWidth;
        }

        element.style.height = globeDim+"px";
        element.style.width = globeDim+"px";
        
        //element.style.top = (logoHeight+24)+"px";

        element.style.top = (logoHeight+24)+"px";
       

        // elementHeight -= parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom);
        // elementWidth -= parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
        // console.log(elementHeight, elementWidth)

        renderer.setSize(globeDim,globeDim);
        renderer.setPixelRatio(window.devicePixelRatio || 1);

        scene = new THREE.Scene();
        //scene.background = null;
        renderer.setClearColor( 0x111111, 0 );

        camera = new THREE.PerspectiveCamera(75, element.clientWidth/element.clientHeight, .001, 1000);
        camera.position.set(0,25,40);

        controls = new OrbitControls(camera, renderer.domElement);
        controls.target = new THREE.Vector3(0,0,0);
        controls.enableZoom = false;
        //if (window.innerWidth < 6400) {
        controls.enableDamping = true;
        controls.dampingFactor = 0.1;
        //}
        controls.minDistance = 30;
        controls.maxDistance = 50;
       

        // const geometry = new THREE.BoxGeometry(10,10,10)
        // const material = new THREE.MeshBasicMaterial({
        //     color: 0x00ff00,
        //     wireframe: false
        // })

        const geometry = new THREE.SphereGeometry( radius-0.15, 100, 50 );
        const material = new THREE.MeshBasicMaterial( { color: 0xffdc2e, wireframe:false } );
        const sphere = new THREE.Mesh( geometry, material );

        //cube = new THREE.Mesh(geometry, material);
        scene.add(sphere);
        //cube.position.set(0,0,0);

        //console.log("cube", cube);

        controls.addEventListener( 'change', render ); 
        controls.addEventListener('start', cancel);
        //controls.addEventListener('end', start);
       
        //render();
        

        getData();
    })

    function cancel() {
        //console.log("stsopping")
        
        cancelAnimationFrame(animationId);
        animationId = null;
        //accum = 0;
        then = null;

        const x = camera.position.x;
        const y = camera.position.y;
        const z = camera.position.z;

        //console.log(Math.sqrt(x*x+y*y+z*z));
    }

    function calcLatLon() {
        const x = camera.position.x;
        const y = camera.position.y;
        const z = camera.position.z;

        const theta = Math.atan2(Math.sqrt(x*x + z*z), y);
        const lat = -theta*(180.0/Math.PI) - 90;
        let lon;
        //console.log("x z", x, z);
        lon = Math.atan2(z, -x);
        lon  = lon * 180.0/Math.PI + 180.;
        accum = lon/10
        //console.log(lon);
    }

    function start() {
        //console.log("starting")
        calcLatLon();
        animationId = requestAnimationFrame(animate);
    }

    function animate(now) {
        now *= 0.001;
        let deltaTime;

        if (then) {
            deltaTime = Math.max(now-then, 0);
        } else {
            deltaTime = 0;
        }
        then = now;

        accum += deltaTime;
        
        const t = Date.now();
        //const post = vertex([t / 100, Math.sin(t / 5000) * 45], radius * 1.75);
        const post = vertex([accum*10, Math.sin(accum/5)*30], radius * 1.75);
        //console.log(t/100);
        //const post = vertex([Math.cos(camera.position.x + deltaTime), camera.position.y + deltaTime], radius * 1.75);
        //console.log(post)
        camera.position.x = post[0];
        camera.position.y = post[1];
        camera.position.z = post[2];
        camera.lookAt(scene.position);
        //controls.update();
        render();
        animationId = requestAnimationFrame(animate);
    }

</script>

<div class="globe-container">
    <div class="canvas-wrapper">
        <canvas class="can" id="can"
        bind:this={canvas}
        >
        </canvas>
        <span class="caption" class:show={state === "RELATIVE" ? true: false}>GFS Precipitable Water<br>
            Analyzed {dateText}
        </span>
    </div>
   
    
</div>

<style>
    .canvas-wrapper {
        position: relative;
        height:auto;
        width:100%;
        display: inline-block;
        padding-top:24px;
        max-width: 800px;
        background-color: #111;
    }

    .caption {
        color:white;
        position:absolute;
        top:0px;
        right:10px;
        opacity:0;
        visibility: hidden;
        transition: opacity 1s; 
        text-align: left;
        font-size: 13px;
    }
    .show {
        opacity:1;
        display:block;
        visibility: visible;
    }
   
    .can {
        height:400px;
        width:400px;
        position:fixed;
        top:400px;
        left:50%;
        transform:translate(-50%,0);
    }

    .globe-container {
        background-color:#111;
        position: relative;
    }

    @media (min-width: 640px) {
		.can {
            top:300px;
        }
        .caption {
            font-size: 16px;
        }
	}
</style>